import React, { useMemo, useState } from 'react';
import { Avatar, Loader } from '@mantine/core';
import styles from '../../../../styles/usageRights.module.css';
import { UsageRightProductDetail, UsageRightRequest } from '../types';
import saralPurpleLogo from '../../../../assets/images/saral-purple-logo.png';
import { Button } from '../../../styledComponents';
import { useApi } from '../../../../hooks/useApi';
import { updateUsageRightRequests } from '../../../../services/usageRights.service';
import { useUsageRight } from '../UsageRightsProvider';
import UsageRequestTag from './UsageRequestTag';
import UsageRightImageContainer from './UsageRightImageContainer';
import { CircleInfo } from '../../../../assets/icons';
import CustomTooltip from '../../../common/CustomTooltip/CustomTooltip';
import AddressUpdateModal from '../../addressUpdateModal/AddressUpdateModal';
import { getCurrencyLogo } from '../../../../utils/global';

interface TermsForRightRequestProps {
  label: string;
  value: string;
  revisedValue?: string;
  tooltipLabel?: string;
}

function TermsForRightRequest({ label, value, revisedValue, tooltipLabel }: TermsForRightRequestProps) {
  const renderValue = () => {
    if (revisedValue) {
      return (
        <div className={styles.termsForRightRequestValueRevised}>
          <span className={styles.termsForRightRequestValueRevisedCanceled}>{value}</span>
          <span className={styles.termsForRightRequestValueRevisedNegotiation}>{revisedValue}</span>
          <CustomTooltip title={`Revised ${tooltipLabel}`}>
            <CircleInfo style={{ cursor: 'pointer' }} />
          </CustomTooltip>
        </div>
      );
    }
    return <div className={styles.termsForRightRequestValue}>{value}</div>;
  };

  return (
    <div className={styles.termsForRightRequest}>
      <div className={styles.termsForRightRequestLabel}>{label}</div>
      {renderValue()}
    </div>
  );
}

interface OfferTermProps {
  value: string;
  revisedValue?: string;
  ugcFreeProducts: UsageRightProductDetail[];
  currency: string;
}

function OfferTerm({ value, revisedValue, ugcFreeProducts, currency }: OfferTermProps) {
  const isPayUgcWithProduct = Boolean(ugcFreeProducts?.length);

  const totalOfferPrice = useMemo(() => {
    let price = 0;
    if (ugcFreeProducts?.length) {
      ugcFreeProducts.forEach((product) => {
        price += parseFloat(product.productDetails.productPrice) * product.quantity;
      });
    }
    return price;
  }, [ugcFreeProducts]);

  return (
    <>
      <TermsForRightRequest
        tooltipLabel="Offer"
        label="💰 Offer"
        value={totalOfferPrice ? `Worth ${getCurrencyLogo(currency)}${totalOfferPrice.toString()}` : value}
        revisedValue={revisedValue}
      />
      {isPayUgcWithProduct && (
        <div>
          <div className={styles.usageRightProductPaymentTitle}>Product(s)</div>
          {ugcFreeProducts.map((product) => (
            <div key={product.variantId} className={styles.usageRightProductPaymentItem}>
              <div className={styles.usageRightProductPaymentItemLeftContainer}>
                <div className={styles.usageRightProductPaymentImageContainer}>
                  <img src={product.productDetails.productImage} alt="" width={52} />
                </div>
                <div>
                  <div className={styles.usageRightProductPaymentProductName}>{product.productDetails.productName}</div>
                  <div className={styles.usageRightProductPaymentProductPrice}>
                    ${product.productDetails.productPrice}
                  </div>
                </div>
              </div>
              <div className={styles.usageRightProductPaymentItemRightContainer}>
                {product?.productDetails?.variantDetail &&
                  Object.entries(product.productDetails.variantDetail).map(([variantName, varianValue]) => (
                    <div key={variantName}>
                      <span className={styles.usageRightProductPaymentItemVariantKey}>{variantName}: </span>
                      <span className={styles.usageRightProductPaymentItemVariantValue}>{varianValue}</span>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

interface UsageRightDetailProps {
  request: UsageRightRequest;
  onClose: () => unknown;
  setUsageRightRevisionOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function UsageRightDetail({ request, onClose, setUsageRightRevisionOpen }: UsageRightDetailProps) {
  const { clearAndRefetch } = useUsageRight();
  const [isAddressOpen, setIsAddressOpen] = useState(false);

  const { triggerFetch: approve, isLoading: isApproving } = useApi(updateUsageRightRequests, {
    type: 'mutation',
    onSuccess: () => {
      onClose();
      clearAndRefetch();
    },
  });

  const { triggerFetch: reject, isLoading: isRejecting } = useApi(updateUsageRightRequests, {
    type: 'mutation',
    onSuccess: () => {
      onClose();
      clearAndRefetch();
    },
  });

  const approveRequest = () => {
    if (isApproving || isRejecting) return;
    approve({
      args: {
        usageRightRequestId: request._id,
        status: 'accepted',
      },
    });
  };

  const rejectRequest = () => {
    if (isApproving || isRejecting) return;
    reject({
      args: {
        usageRightRequestId: request._id,
        status: 'rejected',
      },
    });
  };

  const initialOffer = request?.usageRightHistory?.[0];
  const newOffer = request?.usageRightHistory?.[1];

  const isNewOffer = request?.usageRightHistory?.length > 1;

  const getRevisedDuration = () => {
    if (isNewOffer) return newOffer.duration === -1 ? 'Forever' : `${newOffer.duration} days`;
    return undefined;
  };

  const getRevisedOffer = () => {
    if (isNewOffer) return `${getCurrencyLogo(request.defaultCurrency)}${newOffer.payment}`;
    return undefined;
  };

  if (isAddressOpen) {
    return (
      <div style={{ padding: '18px' }}>
        <AddressUpdateModal
          address={null}
          fetchHomePageData={() => {}}
          profileId={request.profile._id}
          setShowUpdateAddressModal={setIsAddressOpen}
          withoutModal
          modalTitle={
            <div style={{ width: '30ch', textAlign: 'center', fontSize: '18px', margin: '0px 30px' }}>
              Your Address is unavailable, add your address to continue!
            </div>
          }
          actionButtonText="Accept & Continue"
          onSaveComplete={() => {
            setIsAddressOpen(false);
            approveRequest();
          }}
        />
      </div>
    );
  }

  return (
    <div className={styles.usageRightModalContainer}>
      <div className={styles.usageRightModalLeft}>
        <div className={styles.usageRightModalProfileContainer}>
          <Avatar size="60px" radius="xl" src={request?.userPicUrl} />
          <div className={styles.usageRightModalProfileTextContainer}>
            <div>
              <div className={styles.usageRightModalProfileTextTitle}>{request.userName}</div>
              <div className={styles.usageRightModalProfileTextSubTitle}>{request.userRole}</div>
            </div>
            <img src={saralPurpleLogo} alt="" style={{ width: '75px' }} />
          </div>
        </div>
        <div className={styles.usageRightModalTermsContainer}>
          <div className={styles.usageRightModalTermsTitle}>Terms for Rights Request:</div>
          <TermsForRightRequest
            label="📅 Duration"
            tooltipLabel="Duration"
            value={initialOffer.duration === -1 ? 'Forever' : `${initialOffer.duration} days`}
            revisedValue={getRevisedDuration()}
          />
          <TermsForRightRequest label="📝 Purpose" value={request.purpose} />
          <OfferTerm
            value={`${getCurrencyLogo(request.defaultCurrency)}${initialOffer.payment}`}
            revisedValue={getRevisedOffer()}
            ugcFreeProducts={initialOffer.ugcFreeProducts}
            currency={request.defaultCurrency}
          />
        </div>
        <div className={styles.usageRightModalMessageContainer}>
          <div className={styles.usageRightModalMessageTitle}>💌 Message from {request.brandName}</div>
          <div className={styles.usageRightModalMessageSubTitle}>{request.message}</div>
        </div>
        <div className={styles.usageRightModalButtonContainer}>
          {['accepted', 'rejected'].includes(request.status) && <UsageRequestTag status={request.status} />}
          {['pending'].includes(request.status) && (
            <div className={styles.usageRightModalMessageButtonContainer}>
              <Button
                bgColor="var(--purple-p-400)"
                onClick={() => {
                  if (request.addressPresent) {
                    approveRequest();
                  } else {
                    setIsAddressOpen(true);
                  }
                }}
              >
                {isApproving && <Loader color="white" size="xs" />}
                Accept & Approve
              </Button>
              <Button
                onClick={() => setUsageRightRevisionOpen(true)}
                bgColor="white"
                color="var(--purple-p-400)"
                border="1px solid var(--icon-highlight)"
              >
                Send Revised Terms
              </Button>
              <Button bgColor="transparent" color="var(--text-critical)" onClick={() => rejectRequest()}>
                {isRejecting && <Loader color="var(--text-critical)" size="xs" />}
                Reject Offer
              </Button>
            </div>
          )}
          {['negotiation'].includes(request.status) && (
            <Button
              bgColor="transparent"
              color="var(--text-subdued)"
              border="1px solid var(--border-default)"
              style={{ padding: '8px 0px', marginTop: '16px', width: '100%' }}
              onClick={() => onClose()}
            >
              Close
            </Button>
          )}
        </div>
      </div>
      <div style={{ width: '329px' }} className={styles.usageRightModalRight}>
        <UsageRightImageContainer request={request} />
      </div>
    </div>
  );
}

export default UsageRightDetail;
